import type { FC, ReactNode } from 'react'
import type { Tone } from '@/types'

import { useEffect, useRef } from 'react'
import { gsap, prefersReducedMotion } from '@/lib/motion'

export const TONES: Record<Tone, string> = {
    ink: '#151e21',
    graphite: '#394143',
    mist: '#f2f4f5',
    paper: '#ffffff',
    frost: '#c3effd',
    signal: '#0cb7eb'
}

interface ToneCanvasProps {
    initial?: Tone
}

/**
 * One fixed color field behind the page. Whichever [data-tone] section crosses the middle
 * of the viewport sets its color, so chapters blend into each other instead of cutting.
 * Without scripts every section keeps its own solid background.
 */
export const ToneCanvas: FC<ToneCanvasProps> = ({ initial = 'ink' }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const canvas = ref.current

        if (!canvas) return

        const root = document.documentElement
        const reduce = prefersReducedMotion()
        let current: Tone | null = null
        let frame = 0

        const apply = (tone: Tone, instant: boolean) => {
            if (tone === current) return

            current = tone
            gsap.to(canvas, {
                backgroundColor: TONES[tone],
                duration: instant || reduce ? 0 : 0.75,
                ease: 'power2.out',
                overwrite: true
            })
        }

        const probe = (instant = false) => {
            frame = 0

            const line = window.innerHeight * 0.5
            let tone: Tone | null = null

            for (const section of Array.from(document.querySelectorAll<HTMLElement>('[data-tone]'))) {
                const rect = section.getBoundingClientRect()

                if (rect.top <= line && rect.bottom > line) tone = section.dataset.tone as Tone
            }

            if (tone) apply(tone, instant)
        }

        const schedule = () => {
            if (!frame) frame = window.requestAnimationFrame(() => probe())
        }

        root.classList.add('tones-on')
        probe(true)

        window.addEventListener('scroll', schedule, { passive: true })
        window.addEventListener('resize', schedule)
        window.addEventListener('tone:refresh', schedule)

        return () => {
            root.classList.remove('tones-on')
            window.cancelAnimationFrame(frame)
            window.removeEventListener('scroll', schedule)
            window.removeEventListener('resize', schedule)
            window.removeEventListener('tone:refresh', schedule)
        }
    }, [])

    return (
        <div
            ref={ref}
            aria-hidden='true'
            className='pointer-events-none fixed inset-0 z-0'
            style={{ backgroundColor: TONES[initial] }}
        >
            <div className='grain absolute inset-0 opacity-[0.06]' />
        </div>
    )
}
